import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { breakpoints } from '@awaze/design-system.utils.react'
import { AlertBanner } from '@awaze/design-system.components.alert-banner'
import { Box } from '@awaze/design-system.components.box'
import { ResponsiveHeader } from '@awaze/design-system.compositions.responsive-header'
import { Text } from '@awaze/design-system.components.text'

import { selectActiveBanner, selectBrand } from '../../store/selectors'
import NextLink from 'next/link'
import { FooterWrapper } from '../footer-wrapper'
import { FooterData, HeaderData } from '../../../pages/_app'
import { usePBStore } from '../../store/provider'
import { Button } from '@awaze/design-system.components.button'
import { ErrorBoundaryWrapper } from '../../../utils/ErrorBoundaryWrapper'

interface IScreenWrapper {
  headerData: HeaderData | null
  footerData: FooterData | null
  isPageNotFound: boolean
  showPhoneIcon: boolean
  shouldShowWishlistIcon?: boolean
  children: ReactNode[] | ReactNode
}

const OuterContainer = styled('div')`
  @media only screen and (min-width: ${breakpoints[0]}px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const InnerContainer = styled('div')<{ $isFullWidth?: boolean }>`
  @media only screen and (min-width: ${breakpoints[0]}px) {
    margin: 40px 0;
    max-width: ${(props) => (props?.$isFullWidth ? 'none' : '391px')};
    border: 1px solid transparent;
    border-radius: 8px;
    background-color: ${(props) =>
      props?.$isFullWidth ? 'transparent' : '#fff'};
    box-shadow: ${(props) =>
      props?.$isFullWidth ? 'none' : ' 0px 2px 4px rgba(0, 0, 0, 0.2)'};
  }
`

export const ScreenWrapper = ({
  headerData,
  footerData,
  isPageNotFound = false,
  showPhoneIcon = false,
  shouldShowWishlistIcon = false,
  children,
}: IScreenWrapper): ReactNode => {
  const activeBanner = usePBStore(selectActiveBanner)
  const brand = usePBStore(selectBrand)

  // Get wishlist icon from header data (Optimizely experiment for wishlist icon)
  const wishListIcon = headerData?.desktopMenu?.menuItems?.find(
    (item) => item.type === 'wishlist',
  )

  return (
    <>
      {headerData && (
        <ErrorBoundaryWrapper errorSource="screenWrapper.tsx <ResponsiveHeader>">
          <ResponsiveHeader
            wishlist={{
              displayWishlistIcon:
                shouldShowWishlistIcon && !!wishListIcon?.link?.href,
              label: wishListIcon?.link?.displayText || '',
              href: wishListIcon?.link?.href || '',
            }}
            phoneNumber={{
              displayPhoneIcon: showPhoneIcon,
              label: headerData?.desktopMenu.menuItems[0].title ?? '',
              href: headerData?.desktopMenu.menuItems[0].link.href ?? '',
            }}
            brand={brand}
            menuItems={{
              mobileMenu: headerData?.mobileMenu?.menuItems ?? null,
              desktopMenu: headerData?.desktopMenu?.menuItems ?? null,
            }}
            labels={{
              menuButtonText: headerData?.menuButtonText ?? '',
              closeButtonText: headerData?.closeButtonText ?? '',
            }}
            showMegaMenu={false}
          />
        </ErrorBoundaryWrapper>
      )}

      <OuterContainer>
        <InnerContainer $isFullWidth={isPageNotFound}>
          {activeBanner && (
            <Box
              padding={[
                [24, 24, 0, 24],
                [24, 24, 0, 24],
                [32, 32, 0, 32],
              ]}
            >
              <AlertBanner
                variant={activeBanner.variant}
                data-testid={
                  activeBanner.variant === 'constructive'
                    ? 'success-banner'
                    : 'error-banner'
                }
              >
                <Text sizes={[200, 200, 200]}>
                  {activeBanner.link ? (
                    <BannerLinkText
                      text={activeBanner.text}
                      link={activeBanner.link}
                    />
                  ) : (
                    activeBanner.text
                  )}
                </Text>
              </AlertBanner>
            </Box>
          )}
          {children}
        </InnerContainer>
      </OuterContainer>
      {footerData && <FooterWrapper footerData={footerData} />}
    </>
  )
}

const BannerLinkText = ({ text = '', link = '' }) => {
  const start = text.indexOf('%link:start%')
  const end = text.indexOf('%link:end%')
  const linkText = text
    .slice(start, end && end + 10)
    .replace('%link:start%', '')
    .replace('%link:end%', '')

  return (
    <>
      {text.slice(0, start)}
      <NextLink href={link} data-testid="link">
        <Button variant="text" colourScheme="brand-secondary" text={linkText} />
      </NextLink>
      {text.slice(end + 10)}
    </>
  )
}
